import { useFormContext } from 'react-hook-form';
import { Icon } from '@scripsteam/scrips-signal-ds';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface ReminderSectionProps {
  /** Opens the full-screen Reminders picker (Flutter
   * `reminder_profile_selection_widget.dart`). */
  onEdit?: () => void;
}

/**
 * ReminderSection — selected reminder profile card.
 * Mirrors Flutter `reminder_profile_widget.dart`:
 *   - Bell icon + profile name (bold) + edit pencil + delete (×)
 *   - Bullet list of rules ("In-App 1 hour before", etc.) sourced from
 *     `reminderProfileType[]` on the picked ReminderProfile
 *   - Hidden when no reminder is set; the "Reminder" icon-button in the
 *     bottom action row re-opens the picker via `onOpenReminder`.
 */
export function ReminderSection({ onEdit }: ReminderSectionProps = {}) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const reminderProfileId = watch('reminderProfileId');
  const reminderProfileName = watch('reminderProfileName');
  const reminderProfileRules = watch('reminderProfileRules');

  if (!reminderProfileId) return null;

  const clear = () => {
    setValue('reminderProfileId', null, { shouldValidate: true });
    setValue('reminderProfileName', null);
    setValue('reminderProfileRules', []);
  };

  return (
    <div className="booking-section" data-slot="booking-section">
      <p className="booking-section-label">
        Reminder
      </p>
      <div className="booking-card flex items-start">
        <Icon name="bell" size={20} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="booking-card-title truncate">
            {reminderProfileName || 'Reminder set'}
          </p>
          {reminderProfileRules.length > 0 ? (
            <ul className="mt-1 space-y-1">
              {reminderProfileRules.map((rule, i) => (
                <li
                  key={`${rule}-${i}`}
                  className="booking-card-meta"
                >
                  <span aria-hidden className="mr-2 text-text-hint">•</span>
                  {rule}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {onEdit ? (
          <button type="button" aria-label="Edit reminder" onClick={onEdit} className="booking-icon-btn"><Icon name="edit" size={16} aria-hidden /></button>
        ) : null}
        <button type="button" aria-label="Clear reminder" onClick={clear} className="booking-icon-btn"><Icon name="close" size={16} aria-hidden /></button>
      </div>
    </div>
  );
}
